// Sider, der først hentes, når de skal vises (hver i sin egen fil). Så henter bookingsiden ikke kontorets kode,
// steamerens app ikke kontorets moduler, og kontoret kun den side, man står på.
// Er filen allerede hentet (fx af forudHent i sideFiler.ts), vises siden med det samme – uden et kort "Henter …".
import { createElement, use, type ComponentType } from 'react';

type Hentning<M> = Promise<M> & { status?: 'fulfilled' | 'rejected'; value?: M; reason?: unknown };

/** Henter en fil højst én gang ad gangen og husker resultatet, så React kan vise siden med det samme næste gang.
 *  Fejler hentningen (fx netværk), prøves der igen næste gang. */
export function husk<M>(hent: () => Promise<M>): () => Hentning<M> {
  let p: Hentning<M> | null = null;
  return () => {
    if (!p) {
      const ny: Hentning<M> = hent();
      ny.then((v) => { ny.status = 'fulfilled'; ny.value = v; }, (e) => { ny.status = 'rejected'; ny.reason = e; if (p === ny) p = null; });
      p = ny;
    }
    return p;
  };
}

const senere = <M, K extends keyof M>(hent: () => Promise<M>, navn: K) => {
  const Side = (props: object) => createElement(use(hent())[navn] as ComponentType<object>, props);
  Side.displayName = String(navn);
  return Side;
};

/** `const { A, B } = senereSider(FIL.x, 'A', 'B')` – samme komponenter (og props), men hentet ved første visning.
 *  `hent` skal give det samme løfte hver gang (brug husk(), som FIL i sideFiler.ts gør). Vises inden i <Suspense>. */
export const senereSider = <M, K extends keyof M>(hent: () => Promise<M>, ...navne: K[]) =>
  Object.fromEntries(navne.map((n) => [n, senere(hent, n)])) as unknown as { [N in K]: M[N] };
